// Bouton micro pour activer la dictée Web Speech API à côté d'un textarea.
// Toggle start/stop. Au stop, merge le transcript dicté avec la valeur courante
// du textarea (callback `onTranscript(merged)`).

import { useEffect, useRef } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import useSpeechRecognition from "../../hooks/useSpeechRecognition";
import { accumulerDictee, finaliserDictee } from "./speechHelpers";

export default function MicButton({ value, onTranscript, lang = "fr-FR" }) {
  const { supported, motifIndispo, listening, transcript, interimTranscript,
          error, start, stop } = useSpeechRecognition({ lang });
  // 🔴 LA DICTÉE EST UNE BASE + UN TRANSCRIPT, ⛔ PAS UNE SUITE D'AJOUTS.
  //
  // ⚠️ L'OBJECTIF DU « fix dictée dupliquée (2026-05-20) » EST CONSERVÉ — il
  // est même rendu STRUCTUREL. L'ancien montage poussait un DELTA à chaque
  // chunk : il fallait un compteur d'offset pour ne pas ré-envoyer ce qui
  // l'avait déjà été, et cet offset se décalait dès que le moteur révisait un
  // résultat (coupe au milieu d'un mot). Ici on ⛔ n'AJOUTE jamais : on RECALCULE
  // `base + transcript` à chaque fois. Envoyer deux fois le même transcript
  // donne le même texte ⇒ la duplication est IMPOSSIBLE, ⛔ pas seulement
  // évitée — et il n'y a plus d'arithmétique de position à désynchroniser.
  //
  // 🔑 BASE = ce que contenait le champ QUAND LE MICRO A DÉMARRÉ. On la fige,
  // sinon elle inclurait la dictée en cours et le texte se recopierait.
  const baseRef = useRef("");
  const dictaitRef = useRef(false);

  useEffect(() => {
    if (listening && !dictaitRef.current) {
      dictaitRef.current = true;
      baseRef.current = value || "";
    }
    // 🔴 LA MISE EN FORME A LIEU ICI, ET NULLE PART AILLEURS : à l'arrêt du
    // micro, une fois. ⛔ Pas à chaque chunk — un chunk est une respiration,
    // ⛔ pas une phrase, et le ponctuer fabrique des prestations (P0-2).
    if (!listening && dictaitRef.current) {
      dictaitRef.current = false;
      const brut = accumulerDictee(baseRef.current, transcript);
      // N°595 — `{ fin: true }` : C'EST le texte définitif. Les surfaces qui
      // ENREGISTRENT ne le font qu'ici (⛔ pas à chaque mot, ⛔ pas au seul blur).
      if (brut) onTranscript(finaliserDictee(brut), { fin: true });
    }
  }, [listening]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔴 N°596 — PENDANT QU'IL PARLE, ⛔ RIEN N'ENTRE DANS LE CHAMP MÉTIER.
  // ⚠️ Avant : `onTranscript(base + transcript)` à CHAQUE événement — le champ
  // (et, dans le DAL, la BASE via patchField) recevait chaque état
  // intermédiaire, c'est-à-dire tout ce que le moteur du téléphone produisait
  // en route. ⇒ Le texte en cours s'affiche dans l'APERÇU du bouton ; le
  // champ ne reçoit QU'UNE fois le texte final, à l'arrêt (`{ fin: true }`).

  // 🔴 ⛔ PLUS DE `return null`. UNE FONCTION ESSENTIELLE ⛔ NE DISPARAÎT PAS.
  //
  // ⚠️ CE QUE FAISAIT L'ANCIEN CODE : quand le navigateur ⛔ n'expose PAS la
  // reconnaissance vocale, le bouton ⛔ n'était PAS rendu — DU TOUT. L'écran
  // était donc NORMAL, simplement sans micro, et ⛔ RIEN ⛔ ne le disait.
  // 🔑 Le cas nous vise directement : sur iOS, `webkitSpeechRecognition` peut
  // être absente en mode PWA INSTALLÉE alors qu'elle est là dans Safari. Mario
  // aurait ouvert son icône, ⛔ n'aurait pas vu le bouton, et aurait conclu que
  // « ça ne marche pas » — sans que personne ⛔ ne sache pourquoi.
  // ⇒ ÉTAT EXPLICITE : on rend un bouton DÉSACTIVÉ qui NOMME l'indisponibilité
  // et dit le contournement. ⛔ Le comportement quand c'est supporté ⛔ ne change
  // PAS d'un pixel — seule la branche « non supporté » est concernée.
  // 🔴 N°634 — LE BADGE NOMME SA CAUSE, ⛔ il ⛔ ne CONSEILLE PLUS un navigateur.
  //
  // ⚠️ DEUX CONCLUSIONS SUCCESSIVES INFIRMÉES PAR L'USAGE : « c'est Firefox »
  // (JJ voit le badge DANS CHROME), puis mon libellé « ouvrez dans Chrome »
  // — qui s'affiche donc devant quelqu'un DÉJÀ dans Chrome. La capture du
  // 22/09 prouve que SON Chrome expose l'API.
  // 🔑 ⇒ Tant que l'écran dit LA MÊME CHOSE pour des causes DIFFÉRENTES,
  // personne ⛔ ne peut trancher à distance. Il dit désormais LAQUELLE :
  // objet absent · constructeur qui lève (avec le nom de l'erreur) · le mode
  // d'affichage (onglet ou application installée) · le navigateur.
  if (!supported) {
    const modeAffichage = (typeof window !== "undefined"
      && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
      ? "application installée" : "onglet";
    const nav = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const detail = motifIndispo
      ? `${motifIndispo.cause} — ${motifIndispo.detail}`
      : "cause non déterminée";
    return (
      <span
        role="status"
        data-testid="dal-mic-indisponible"
        data-cause={motifIndispo ? motifIndispo.cause : "inconnue"}
        data-mode-affichage={modeAffichage}
        title={`Dictée indisponible : ${detail}. Mode : ${modeAffichage}. `
               + `Navigateur : ${nav.slice(0, 120)}. Saisissez le texte.`}
        className="flex items-center gap-1 text-xs px-2 py-1 rounded"
        style={{ background: "var(--oh-stat-amber-bg)",
                 color: "var(--oh-stat-amber-text)" }}
      >
        <MicOff className="w-3 h-3" />
        Dictée indisponible sur ce navigateur — saisissez le texte
      </span>
    );
  }

  const handleToggle = () => {
    if (listening) stop();
    else start();
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      title={listening ? "Arrêter la dictée" : "Dicter"}
      data-testid="dal-mic-btn"
      /* 🔴🔴 N°673-2 — C'EST **LE** GESTE DU MÉTIER, et il faisait 24 px.
         ⚠️ MESURÉ sur le servi : `text-xs px-2 py-1` + icône 12 px = une cible
         de 24 px de haut, pour une main qui tient une lampe ou une échelle.
         La règle de l'écran terrain est 48 px (cf. l'en-tête de
         MobileVisitePage) : ⛔ ce bouton ⛔ n'y était PAS soumis.
         ⇒ min-h-[44px], padding vertical py-3, texte et icône lisibles.
         ⛔ Aucune logique touchée : la dictée elle-même ⛔ ne bouge pas. */
      className={`flex items-center gap-2 text-sm px-3 py-3 min-h-[44px] rounded-lg transition-colors ${
        listening
          ? "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] ring-1 ring-red-500/50 animate-pulse"
          : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] hover:bg-slate-600/50"
      }`}
    >
      {error ? <MicOff className="w-5 h-5" /> : listening ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
      {listening ? "Stop" : "Dicter"}
      {listening && (transcript || interimTranscript) && (
        <span className="text-[9px] italic max-w-[160px] truncate"
              data-testid="dal-mic-apercu">
          {[transcript, interimTranscript].filter(Boolean).join(" ").slice(-60)}
        </span>
      )}
      {/* 🔴 L'ERREUR EST DITE, ⛔ pas réduite à une icône barrée. Un micro
          refusé (permission) et un micro absent ⛔ ne se corrigent PAS pareil :
          l'un se ré-autorise, l'autre impose de changer de contexte. */}
      {error && !listening && (
        <span className="text-[9px] max-w-[140px] truncate"
              data-testid="dal-mic-erreur"
              style={{ color: "var(--oh-stat-red-text)" }}>
          micro refusé — vérifiez l'autorisation
        </span>
      )}
    </button>
  );
}
